import { useSpaceIdFromPathname } from 'hooks/useSpaceInfoFromPathname'
import {
    blockchainQueryKeys,
    BlockchainTransactionType,
    TSigner,
    useBaseReads,
    useBaseWrites,
    useTransaction,
} from 'use-towns-client'

import { useUSDCMetadata } from '../UserOpTxModal/usdc/qr-fund/useUSDCAddress'

export type UpgradeToUsdcData = {
    usdcAmount: bigint
    spaceId: string
    currentPrice: bigint
}

export function useUpgradeToUsdc({
    onSuccess,
    onError,
}: {
    onSuccess?: () => void
    onError?: (error: Error) => void
} = {}) {
    const spaceId = useSpaceIdFromPathname()
    const { spaceDapp } = useBaseReads()
    const { baseTransactor, userOpsInstance } = useBaseWrites()
    const { address: usdcAddress } = useUSDCMetadata()

    return useTransaction({
        debugName: 'useUpgradeToUsdc',
        onSuccess: () => {
            onSuccess?.()
        },
        onError: (error) => {
            onError?.(error)
        },
        execute: async ({ data, signer }: { data: UpgradeToUsdcData; signer: TSigner }) => {
            const smartAccount = await userOpsInstance?.smartAccountSession.getSmartAccount({
                signer,
            })
            if (!smartAccount) {
                throw new Error('Smart account not found')
            }
            return baseTransactor.createTransaction({
                signer,
                type: BlockchainTransactionType.ChangeCurrencyAndPrice,
                debugName: 'upgradeToUsdcMembership',
                contextData: data,
                aaExecute: async () => {
                    if (!spaceId) {
                        throw new Error('Space ID not found')
                    }

                    const space = spaceDapp.getSpace(spaceId)
                    if (!space) {
                        throw new Error('Space not found')
                    }
                    const currentPrice = data.currentPrice

                    const toAddress: string[] = [space.Membership.address, space.Membership.address]
                    const callData: string[] = [
                        space.Membership.encodeFunctionData('setMembershipCurrency', [usdcAddress]),
                        space.Membership.encodeFunctionData('setMembershipPrice', [
                            data.usdcAmount,
                        ]),
                    ]

                    // changing from free to paid space - remove all free allocations
                    if (currentPrice === 0n && data.usdcAmount > 0n) {
                        const freeAllocationCallData = space.Membership.encodeFunctionData(
                            'setMembershipFreeAllocation',
                            [0],
                        )
                        toAddress.push(space.Membership.address)
                        callData.push(freeAllocationCallData)
                    }

                    return smartAccount.sendUserOp({
                        toAddress,
                        callData,
                        signer,
                        spaceId,
                        functionHashForPaymasterProxy: 'upgradeToUsdcMembership',
                    })
                },
                eoaExecute: () => {
                    throw new Error('Not implemented')
                },
                errorDecoder: (error) => {
                    return baseTransactor.getDecodedErrorForSpace(spaceId ?? '', error)
                },
            })
        },
        invalidateQueries: (params) => {
            const spaceId = params.data.spaceId
            return [
                blockchainQueryKeys.membershipInfo(spaceId),
                blockchainQueryKeys.membershipPrice(spaceId),
            ]
        },
    })
}
